import React, { useEffect, useState } from "react";
import { Container, Button, Spinner } from "react-bootstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Breadcrumbs from "../../../Common/Breadcrumbs";
import { SuccessModal } from "../../../Common/SuccessModal";
import { PermissionWrapper } from "../../../components/PermissionWrapper/PermissionWrapper";
import { ENROLMENTS_MANAGE_PERMS } from "../permissions";
import EnrolmentForm from "../EnrolmentForm";
import OfflineEnrolmentForm from "../EnrolmentForm/offline/OfflineEnrolmentForm";
import {
  getEnrolmentDetails,
  getBatchById,
  type EnrolmentDetails,
  type BatchDetails,
} from "../../../api/academyEnrolments/enrolmentsService";
import "../AcademyEnrolments.css";

type PageMode = "view" | "edit";

const EnrolmentDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const startInEdit = (location.state as { startInEdit?: boolean } | null)?.startInEdit ?? false;

  const [pageMode, setPageMode] = useState<PageMode>(startInEdit ? "edit" : "view");
  const [enrolment, setEnrolment] = useState<EnrolmentDetails | null>(null);
  const [batch, setBatch] = useState<BatchDetails | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Snapshot of initial data to restore on cancel
  const [snapshotEnrolment] = useState<EnrolmentDetails | null>(null);
  const [snapshotBatch] = useState<BatchDetails | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoadingData(true);
    setLoadError(null);

    getEnrolmentDetails(id)
      .then(async (details) => {
        setEnrolment(details);
        if (details.batch) {
          const batchData = await getBatchById(details.batch);
          setBatch(batchData);
        }
      })
      .catch((err: unknown) => {
        const anyErr = err as any;
        const msg =
          anyErr?.response?.data?.error ||
          anyErr?.response?.data?.message ||
          "Failed to load enrolment details.";
        setLoadError(msg);
      })
      .finally(() => setLoadingData(false));
  }, [id]);

  const handleEditClick = () => {
    setPageMode("edit");
  };

  const handleCancelEdit = () => {
    setPageMode("view");
  };

  const handleSuccess = () => {
    setShowSuccess(true);
  };

  const handleSuccessClose = async () => {
    setShowSuccess(false);
    // Re-fetch fresh data and revert to view mode
    if (id) {
      setLoadingData(true);
      try {
        const details = await getEnrolmentDetails(id);
        setEnrolment(details);
        if (details.batch) {
          const batchData = await getBatchById(details.batch);
          setBatch(batchData);
        } else {
          setBatch(null);
        }
      } catch {
        /* ignore refresh errors */
      } finally {
        setLoadingData(false);
      }
    }
    setPageMode("view");
  };

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3 mt-4" style={{ padding: "0 15px" }}>
          <Breadcrumbs
            items={[
              { label: "Academy" },
              {
                label: "Enrolments Management",
                onClick: () => navigate("/academy-enrolments"),
              },
              {
                label: pageMode === "edit" ? "Edit Enrolment" : "View Enrolment",
                active: true,
              },
            ]}
          />
        </div>

        {/* Header */}
        <div
          className="d-flex align-items-center justify-content-between mb-4"
          style={{ padding: "0 15px" }}
        >
          <div>
            <h2 className="mb-0">
              {pageMode === "edit" ? "Edit Enrolment" : "View Enrolment"}
            </h2>
            {enrolment && (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                {enrolment.player?.fullName} &middot; {enrolment.academy?.basicDetails?.academyName}
              </p>
            )}
          </div>

          <div className="d-flex gap-2">
            {pageMode === "view" ? (
              <PermissionWrapper
                anyOfPermissions={ENROLMENTS_MANAGE_PERMS}
                hideWhenDisabled
              >
                <Button
                  variant="outline-primary"
                  className="d-flex align-items-center gap-1"
                  onClick={handleEditClick}
                  disabled={loadingData}
                >
                  <i className="ri-edit-line" />
                  Edit
                </Button>
              </PermissionWrapper>
            ) : (
              <Button
                variant="outline-secondary"
                className="d-flex align-items-center gap-1"
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
            )}

            <Button
              variant="outline-secondary"
              className="d-flex align-items-center gap-1"
              onClick={() => navigate("/academy-enrolments")}
            >
              <i className="ri-arrow-left-line" />
              Back
            </Button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: "0 15px 40px" }}>
          {loadingData ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
              <Spinner animation="border" variant="primary" />
            </div>
          ) : loadError ? (
            <div className="enrolment-submit-error">
              <i className="ri-error-warning-line me-2" />
              {loadError}
            </div>
          ) : ["offline", "partner app - offline"].includes(
            (enrolment?.enrolledThrough ?? "").toLowerCase()
          ) ? (
            <OfflineEnrolmentForm
              mode={pageMode === "view" ? "view" : "edit"}
              initialEnrolment={enrolment}
              initialBatch={batch}
              onSuccess={handleSuccess}
            />
          ) : (
            <EnrolmentForm
              mode={pageMode}
              initialEnrolment={enrolment}
              initialBatch={batch}
              onSuccess={handleSuccess}
            />
          )}
        </div>
      </Container>

      <SuccessModal
        show={showSuccess}
        handleClose={handleSuccessClose}
        title="Enrolment Updated!"
        message="The enrolment has been successfully updated."
        buttonLabel="OK"
      />
    </div>
  );
};

export default EnrolmentDetailsPage;
